import { HEN_BREEDS, BREED_IDS } from '../data/breeds';
import { UPGRADES_BY_ID } from '../data/upgrades';
import { BARNEVELDER_THRESHOLD_CUT } from '../data/balance';
import type { BreedId, Requirement, UnlockRule } from '../data/types';
import { wranglerThresholdMult } from './specials';
import { countHens, countRoosters, emit, ownedBreedCount, ownedBreeds, touch, logActivity } from './state';
import type { GameState } from './types';

export interface Progress {
  current: number;
  target: number;
}

function rareBreedsOwned(s: GameState): number {
  let n = 0;
  for (const b of ownedBreeds(s)) if (HEN_BREEDS[b].tier === 'RARE') n++;
  return n;
}

export function reqProgress(s: GameState, r: Requirement): Progress {
  switch (r.kind) {
    case 'hens': return { current: countHens(s), target: r.count };
    case 'roosters': return { current: countRoosters(s), target: r.count };
    case 'ownBreed': return { current: ownedBreedCount(s, r.breed), target: r.count };
    case 'upgrade': return { current: s.upgrades.includes(r.id) ? 1 : 0, target: 1 };
    case 'lifetimeEarned': return { current: s.lifetimeEarned, target: r.amount };
    case 'goldenClicked': return { current: s.stats.goldenClicked, target: r.count };
    case 'silverClicked': return { current: s.stats.silverClicked, target: r.count };
    case 'achievements':
      return { current: s.achievements.length, target: Math.ceil(r.count * wranglerThresholdMult(s, BARNEVELDER_THRESHOLD_CUT)) };
    case 'rareBreedsOwned': return { current: rareBreedsOwned(s), target: r.count };
  }
}

export function reqMet(s: GameState, r: Requirement): boolean {
  const p = reqProgress(s, r);
  return p.current >= p.target;
}

export function reqsMet(s: GameState, reqs: readonly Requirement[]): boolean {
  return reqs.every((r) => reqMet(s, r));
}

export function reqText(s: GameState, r: Requirement): string {
  const p = reqProgress(s, r);
  switch (r.kind) {
    case 'hens': return `Own ${p.target} hens`;
    case 'roosters': return `Own ${p.target} rooster${p.target === 1 ? '' : 's'}`;
    case 'ownBreed': return `Own ${p.target} ${HEN_BREEDS[r.breed].name}`;
    case 'upgrade': return `Buy ${UPGRADES_BY_ID.get(r.id)?.name ?? r.id}`;
    case 'lifetimeEarned': return `Earn $${p.target.toLocaleString('en-US')} lifetime`;
    case 'goldenClicked': return `Click ${p.target} golden egg${p.target === 1 ? '' : 's'}`;
    case 'silverClicked': return `Click ${p.target} silver egg${p.target === 1 ? '' : 's'}`;
    case 'achievements': return `Earn ${p.target} achievements`;
    case 'rareBreedsOwned': return `Own ${p.target} RARE breeds`;
  }
}

/** First unmet requirement (for "locked" labels), or null. */
export function firstUnmet(s: GameState, reqs: readonly Requirement[]): Requirement | null {
  return reqs.find((r) => !reqMet(s, r)) ?? null;
}

// ── Breed unlocks ─────────────────────────────────────────────────────

export function unlockRuleMet(s: GameState, rule: UnlockRule): boolean {
  switch (rule.kind) {
    case 'start': return true;
    case 'hens': return countHens(s) >= rule.count;
    case 'roosters': return countRoosters(s) >= rule.count;
  }
}

export function unlockRuleText(rule: UnlockRule): string {
  switch (rule.kind) {
    case 'start': return 'Available from the start';
    case 'hens': return `Unlocks at ${rule.count} hens`;
    case 'roosters': return rule.count === 1 ? 'Unlocks with your first rooster' : `Unlocks at ${rule.count} roosters`;
  }
}

export function unlockProgress(s: GameState, rule: UnlockRule): Progress {
  switch (rule.kind) {
    case 'start': return { current: 1, target: 1 };
    case 'hens': return { current: countHens(s), target: rule.count };
    case 'roosters': return { current: countRoosters(s), target: rule.count };
  }
}

/** Unlocks newly eligible breeds. Returns the breeds unlocked this call. */
export function checkBreedUnlocks(s: GameState): BreedId[] {
  const fresh: BreedId[] = [];
  for (const id of BREED_IDS) {
    if (s.unlockedBreeds.includes(id)) continue;
    if (!unlockRuleMet(s, HEN_BREEDS[id].unlock)) continue;
    s.unlockedBreeds.push(id);
    s.newBreeds.push(id);
    fresh.push(id);
    emit(s, { kind: 'breedUnlocked', breed: id });
    logActivity(s, 'milestone', 'New breed', HEN_BREEDS[id].name);
  }
  if (fresh.length) touch(s);
  return fresh;
}
